/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { onClickNavUrl } from '@coursera-web/cds-labs';

import useRouter from 'js/lib/useRouter';

import { Link, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { ContentfulHeaderData } from 'bundles/ent-website/components/EntWebsitePageHeader';
import C4BLogo from 'bundles/ent-website/components/c4bLogo';
import C4CLogo from 'bundles/ent-website/components/c4cLogo';
import C4GLogo from 'bundles/ent-website/components/c4gLogo';
import C4TLogo from 'bundles/ent-website/components/c4tLogo';
import ContentfulLogo from 'bundles/ent-website/components/contentfulLogo';
import EnterpriseLogo from 'bundles/ent-website/components/enterpriseLogo';
import PartnershipLogo from 'bundles/ent-website/components/partnershipLogo';
import SocialImpactLogo from 'bundles/ent-website/components/socialImpactLogo';
import { DOMAINS, DOMAIN_URL_MAPPINGS } from 'bundles/ent-website/constants/navigation';
import { eventingTransform } from 'bundles/ent-website/utils/eventingTransform';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

type HeaderLogoProps = {
  domain: keyof typeof DOMAINS;
  contentfulHeaderData?: ContentfulHeaderData;
};

const HeaderLogo = ({ domain, contentfulHeaderData }: HeaderLogoProps) => {
  const styles = {
    logo: () => css`
      margin-right: 72px;
      line-height: 1;

      ${breakpoints.down('sm')} {
        margin-right: var(--cds-spacing-150);
      }

      > img {
        vertical-align: baseline;
      }
    `,
  };

  const router = useRouter();
  const trackingName = eventingTransform('navigation_header_logo');
  const track = useTracker();

  const logo = () => {
    if (contentfulHeaderData?.logo?.url) {
      return <ContentfulLogo src={contentfulHeaderData.logo.url} height={contentfulHeaderData?.logoHeight ?? 25} />;
    }
    if (domain === DOMAINS.c4g) {
      return <C4GLogo />;
    } else if (domain === DOMAINS.c4t) {
      return <C4TLogo />;
    } else if (domain === DOMAINS.c4c) {
      return <C4CLogo />;
    } else if (domain === DOMAINS.enterprise) {
      return <EnterpriseLogo />;
    } else if (domain === DOMAINS.partners) {
      return <PartnershipLogo />;
    } else if (domain === DOMAINS.impact) {
      return <SocialImpactLogo />;
    }

    return <C4BLogo />;
  };

  const targetUrl = contentfulHeaderData?.logoTarget ?? `/${DOMAIN_URL_MAPPINGS[domain]}`;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    track('click_button', {
      button: {
        name: 'ent_website_navigation_header_logo',
      },
    });
    onClickNavUrl(router, targetUrl)(e);
  };
  return (
    <div css={styles.logo}>
      <Link component={TrackedA} trackingName={trackingName} variant="quiet" href={targetUrl} onClick={handleClick}>
        {logo()}
      </Link>
    </div>
  );
};

export default HeaderLogo;
